import {
  Card,
  Stack,
  Text,
  Switch,
  Group,
  Divider,
  Title,
  Button,
  LoadingOverlay,
} from '@mantine/core';
import React, { useState, useEffect } from 'react';

import {
  useGetNotificationPreferences,
  useUpdateNotificationPreferences,
  NotificationPreferences,
} from 'api/notificationPreferences';

function NotificationPreferencesComponent() {
  const { data, isLoading } = useGetNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(
    null
  );

  useEffect(() => {
    if (data?.preferences) {
      setPreferences(data.preferences);
    }
  }, [data]);

  const handleUpdate = (channel: 'email' | 'inApp', key: string, value: boolean) => {
    if (!preferences) {
      return;
    }

    const updated = {
      ...preferences,
      [channel]: {
        ...preferences[channel],
        [key]: value,
      },
    };
    setPreferences(updated);
  };

  const handleSave = () => {
    if (!preferences || !data?.preferences) {
      return;
    }

    const changes: {
      email?: Partial<NotificationPreferences['email']>;
      inApp?: Partial<NotificationPreferences['inApp']>;
    } = {};

    // Only send changed values
    Object.keys(preferences.email).forEach((key) => {
      const typedKey = key as keyof NotificationPreferences['email'];
      if (preferences.email[typedKey] !== data.preferences.email[typedKey]) {
        if (!changes.email) {
          changes.email = {};
        }
        changes.email[typedKey] = preferences.email[typedKey];
      }
    });

    Object.keys(preferences.inApp).forEach((key) => {
      const typedKey = key as keyof NotificationPreferences['inApp'];
      if (preferences.inApp[typedKey] !== data.preferences.inApp[typedKey]) {
        if (!changes.inApp) {
          changes.inApp = {};
        }
        changes.inApp[typedKey] = preferences.inApp[typedKey];
      }
    });

    if (Object.keys(changes).length > 0) {
      updatePreferences.mutate(changes);
    }
  };

  const hasChanges =
    preferences &&
    data?.preferences &&
    JSON.stringify(preferences) !== JSON.stringify(data.preferences);

  if (isLoading || !preferences) {
    return (
      <Card withBorder p="xl" style={{ position: 'relative' }}>
        <LoadingOverlay visible />
      </Card>
    );
  }

  const notificationTypes = [
    { key: 'mentions', label: 'Mentions' },
    { key: 'assignments', label: 'Assignments' },
    { key: 'statusChanges', label: 'Status Changes' },
    { key: 'comments', label: 'Comments' },
    { key: 'dueDates', label: 'Due Dates' },
    { key: 'projectInvites', label: 'Project Invites' },
  ] as const;

  return (
    <Card withBorder p="xl">
      <Stack spacing="lg">
        <Title order={3}>Notification Preferences</Title>
        <Text size="sm" color="dimmed">
          Choose how you want to be notified about different events.
        </Text>

        <Divider />

        <Stack spacing="md">
          <Title order={4}>Email Notifications</Title>
          {notificationTypes.map((type) => (
            <Group key={type.key} position="apart">
              <Text size="sm">{type.label}</Text>
              <Switch
                checked={preferences.email[type.key]}
                onChange={(event) =>
                  handleUpdate('email', type.key, event.currentTarget.checked)
                }
              />
            </Group>
          ))}
        </Stack>

        <Divider />

        <Stack spacing="md">
          <Title order={4}>In-App Notifications</Title>
          {notificationTypes.map((type) => (
            <Group key={type.key} position="apart">
              <Text size="sm">{type.label}</Text>
              <Switch
                checked={preferences.inApp[type.key]}
                onChange={(event) =>
                  handleUpdate('inApp', type.key, event.currentTarget.checked)
                }
              />
            </Group>
          ))}
        </Stack>

        {hasChanges && (
          <Group position="right" mt="md">
            <Button
              variant="outline"
              onClick={() => setPreferences(data?.preferences || null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={updatePreferences.isLoading}
            >
              Save Changes
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

export default NotificationPreferencesComponent;
